import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuditOverview } from '@/audit/api'
import { Alert, AlertDescription } from '@/components/ui/alert'

/** Where the spec/security/ governance docs live (blob view on the spec repo). */
const SPEC_REPO_BLOB = 'https://github.com/RandiCsapat/igazirandi-spec-v0.1/blob/main/spec/security'

/** The governance-document set — static reference (the docs live in the spec
 *  repo, not the runtime). Shown with a link so an ops admin / auditor can open
 *  each one. */
const GOVERNANCE_DOCS: { file: string; key: string }[] = [
  { file: 'audit-readiness.md', key: 'auditReadiness' },
  { file: 'ibsz.md', key: 'ibsz' },
  { file: 'incident-response.md', key: 'incidentResponse' },
  { file: 'risk-assessment.md', key: 'riskAssessment' },
  { file: 'key-cert-management.md', key: 'keyCert' },
  { file: 'subprocessors.md', key: 'subprocessors' },
  { file: 'bcp-drp.md', key: 'bcpDrp' },
  { file: 'data-flow.md', key: 'dataFlow' },
  { file: 'access-control-register.md', key: 'accessControl' },
]

function Bool({ value }: { value: boolean }) {
  return (
    <span className={value ? 'font-medium' : 'text-muted-foreground'}>{value ? '✓' : '✕'}</span>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2 rounded-lg border p-4">
      <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-sm">{children}</dl>
    </section>
  )
}

/** A label (+ where the value is defined: env / config / DEC) and its value. */
function Row({ label, source, children }: { label: string; source: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex flex-col">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-[10px] leading-tight text-muted-foreground/70">
          {source}
        </span>
      </dt>
      <dd className="text-right font-mono">{children}</dd>
    </>
  )
}

/**
 * `/audit` — operations-surface read-only "audit overview" (DEC-ADMIN-012 /
 * DEC-ADMIN-013). The live security posture in one place — every value read by
 * the backend from the running config/DB; each row shows WHERE the value is
 * defined (env / config / DEC). Plus links to the spec/security governance docs.
 */
export function AuditOverview() {
  const { t } = useTranslation()
  const q = useAuditOverview()
  const data = q.data

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="self-start text-sm text-primary underline-offset-4 hover:underline">
          {t('audit.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('audit.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('audit.intro')}</p>
      </header>

      {q.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {q.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('audit.loadError')}</AlertDescription>
        </Alert>
      )}

      {!q.isPending && !q.isError && data && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Section title={t('audit.sections.environment')}>
            <Row label={t('audit.fields.env')} source="APP_ENV">
              {data.environment.env}
            </Row>
            <Row label={t('audit.fields.debug')} source="APP_DEBUG">
              <Bool value={data.environment.debug} />
            </Row>
            <Row label={t('audit.fields.url')} source="APP_URL">
              {data.environment.url}
            </Row>
          </Section>

          <Section title={t('audit.sections.transport')}>
            <Row label={t('audit.fields.hsts')} source="SECURITY_HSTS_ENABLED · DEC-SECURITY-001">
              <Bool value={data.transport_security.hsts_enabled} />
            </Row>
            <Row label={t('audit.fields.hstsMaxAge')} source="SECURITY_HSTS_MAX_AGE">
              {data.transport_security.hsts_max_age}
            </Row>
          </Section>

          <Section title={t('audit.sections.auth')}>
            <Row label={t('audit.fields.admin2fa')} source="DEC-ADMIN-011">
              <Bool value={data.authentication.admin_2fa_enforced} />
            </Row>
            <Row label={t('audit.fields.passwordMin')} source="RegisterRequest">
              {data.authentication.password_min_length}
            </Row>
          </Section>

          <Section title={t('audit.sections.rateLimiting')}>
            <Row
              label={t('audit.fields.namedLimiters')}
              source="config/rate_limits.php · DEC-RATE-001"
            >
              {data.rate_limiting.named_limiters}
            </Row>
            <dt className="col-span-2 text-xs">
              <Link to="/rate-limits" className="text-primary underline-offset-4 hover:underline">
                {t('audit.rateLimitsLink')}
              </Link>
            </dt>
          </Section>

          <Section title={t('audit.sections.retention')}>
            <Row
              label={t('audit.fields.loginEvents')}
              source="LOG_RETENTION_LOGIN_EVENTS_DAYS · DEC-OPS-007"
            >
              {data.log_retention_days.login_events} {t('audit.days')}
            </Row>
            <Row label={t('audit.fields.auditLogs')} source="LOG_RETENTION_AUDIT_LOGS_DAYS">
              {data.log_retention_days.audit_logs} {t('audit.days')}
            </Row>
            <Row label={t('audit.fields.dapAuditLogs')} source="LOG_RETENTION_DAP_AUDIT_LOGS_DAYS">
              {data.log_retention_days.dap_audit_logs} {t('audit.days')}
            </Row>
          </Section>

          <Section title={t('audit.sections.maintenance')}>
            <Row label={t('audit.fields.maintenanceEnabled')} source="DEC-OPS-008">
              <Bool value={data.maintenance.enabled} />
            </Row>
            <dt className="col-span-2 text-xs">
              <Link to="/maintenance" className="text-primary underline-offset-4 hover:underline">
                {t('audit.maintenanceLink')}
              </Link>
            </dt>
          </Section>

          <Section title={t('audit.sections.dap')}>
            <Row label={t('audit.fields.dapMode')} source="DAP_DEFAULT_MODE">
              {data.dap.default_mode}
            </Row>
            <Row label={t('audit.fields.dapAllowed')} source="DAP_ALLOWED_MODES">
              {data.dap.allowed_modes.join(', ') || '—'}
            </Row>
            <Row label={t('audit.fields.dapTrust')} source="DAP_TRUST_MODE">
              {data.dap.trust_mode}
            </Row>
          </Section>

          <Section title={t('audit.sections.monitoring')}>
            <Row label={t('audit.fields.sentry')} source="SENTRY_ENABLED · DEC-OPS-003">
              <Bool value={data.monitoring.sentry_enabled} />
            </Row>
          </Section>
        </div>
      )}

      <section className="flex flex-col gap-2 rounded-lg border p-4">
        <h2 className="text-sm font-semibold tracking-tight">{t('audit.sections.governance')}</h2>
        <p className="text-xs text-muted-foreground">{t('audit.governanceIntro')}</p>
        <ul className="flex flex-col gap-1 text-sm">
          {GOVERNANCE_DOCS.map((doc) => (
            <li key={doc.file} className="flex flex-wrap items-baseline gap-x-2">
              <a
                href={`${SPEC_REPO_BLOB}/${doc.file}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs text-primary underline-offset-4 hover:underline"
              >
                {doc.file}
              </a>
              <span className="text-muted-foreground">— {t(`audit.docs.${doc.key}`)}</span>
            </li>
          ))}
        </ul>
      </section>
    </main>
  )
}
